export type AppEnv = 'staging' | 'production' | 'development';

function readMode(): AppEnv {
  const mode = typeof __APP_ENV__ === 'string' ? __APP_ENV__ : 'development';
  if (mode === 'staging' || mode === 'production') return mode;
  return 'development';
}

/** Which database this build uses. Comes from the Vite `--mode`, like the extension. */
export const APP_ENV: AppEnv = readMode();

/** Comma-separated ids to try when looking for the installed extension (store build, unpacked build). */
export function parseExtensionIds(raw: string | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
}

/**
 * The id of an unpacked development or staging build of the extension. Those builds carry a fixed
 * public key in their manifest (packages/extension/build/extensionKey.ts), so the id never
 * changes.
 */
export const UNPACKED_EXTENSION_ID = 'jicghjbbdpifeijnpajdeemaokljeajk';

/**
 * The extension's id in the Chrome Web Store. An unpacked production build made with the store
 * item's public key (`VITE_EXTENSION_KEY`) has the same id.
 */
export const STORE_EXTENSION_ID = 'eeooebokdnjfibhbmhblihbphbnpbkng';
export const STORE_PAGE_URL = `https://chromewebstore.google.com/detail/lingoping/${STORE_EXTENSION_ID}`;

/**
 * The ids to try: the one that belongs to this site (the store version on production, the
 * unpacked build on development and staging), then any others from the build settings.
 */
export function extensionIdsFor(env: AppEnv, configured: string | undefined): string[] {
  const own = env === 'production' ? STORE_EXTENSION_ID : UNPACKED_EXTENSION_ID;
  return [own, ...parseExtensionIds(configured).filter((id) => id !== own)];
}

export const EXTENSION_IDS: string[] = extensionIdsFor(APP_ENV, import.meta.env.VITE_EXTENSION_IDS);

/** Where to get the extension: the store page, unless the build settings name another https page. */
export function parseStoreUrl(raw: string | undefined): string {
  const url = (raw ?? '').trim();
  return /^https:\/\//i.test(url) ? url : STORE_PAGE_URL;
}

export const EXTENSION_STORE_URL: string = parseStoreUrl(import.meta.env.VITE_EXTENSION_STORE_URL);

/**
 * Paddle's client-side token for the checkout (`VITE_PADDLE_CLIENT_TOKEN`) with the environment it
 * belongs to. Sandbox tokens start with `test_`, live ones with `live_`: a token that does not
 * match the build (a live token on staging would charge real cards) is refused, so the subscribe
 * page then says checkout isn't available.
 */
export function paddleConfigFor(
  env: AppEnv,
  token: string | undefined
): { token: string; environment: 'sandbox' | 'production' } | null {
  const value = (token ?? '').trim();
  if (!value) return null;
  if (env === 'production') {
    return value.startsWith('live_') ? { token: value, environment: 'production' } : null;
  }
  return value.startsWith('test_') ? { token: value, environment: 'sandbox' } : null;
}

export const PADDLE_CONFIG = paddleConfigFor(APP_ENV, import.meta.env.VITE_PADDLE_CLIENT_TOKEN);
